import React, { useState, useEffect } from 'react';
import axios from 'axios';
import getEnvironmentConfig from '../config/environment';
import {
  fetchTemplateDocuments,
  fetchDocumentsByPhase
} from '../services/s3Service';
import { downloadDocument, downloadDocumentAuthenticated } from '../services/documentService';
import { getActiveSessionId } from '../utils/sessionManagerV2';

function MissionControlCenter({
  docUrl,
  docContent,
  setDocContent,
  isPackage,
  packageUrls,
  DOC_CREATION_API_BASE
}) {
  const [templateDocuments, setTemplateDocuments] = useState([]);
  const [phaseOneDocuments, setPhaseOneDocuments] = useState([]);
  const [phaseTwoDocuments, setPhaseTwoDocuments] = useState([]);
  const [loading, setLoading] = useState({
    templates: false,
    phaseOne: false,
    phaseTwo: false
  });
  const [error, setError] = useState(null);
  const [expandedTemplate, setExpandedTemplate] = useState(null);
  const [sessionId, setSessionId] = useState(null);

  // Get session ID on mount
  useEffect(() => {
    const fetchSessionId = async () => {
      try {
        const activeSessionId = await getActiveSessionId();
        setSessionId(activeSessionId);
        console.log('MissionControlCenter: Got sessionId:', activeSessionId);
      } catch (error) {
        console.error('MissionControlCenter: Error getting sessionId:', error);
      }
    };
    fetchSessionId();
  }, []);

  // Get API endpoints from environment configuration
  const config = getEnvironmentConfig();
  const aiDraftingEndpoint = config.aws_cloud_logic_custom.find(api => api.name === 'AIDraftingAPI')?.endpoint;
  const DOC_PROCESSING_API_URL = aiDraftingEndpoint ? `${aiDraftingEndpoint}/dev/docProcessing` : null;

  useEffect(() => {
    // Fetch all document data on component mount
    loadAllDocuments();
  }, []);

  const loadAllDocuments = async () => {
    // Fetch template documents
    fetchTemplateDocumentsData();
    // Fetch Phase I and Phase II documents
    fetchAllPhaseDocuments();
  };

  const fetchTemplateDocumentsData = async () => {
    setLoading(prev => ({ ...prev, templates: true }));
    setError(null);
    try {
      const response = await fetchTemplateDocuments();
      if (response.success) {
        // Flatten the template structure for easier rendering
        const flatTemplates = [];
        response.templates.forEach(categoryGroup => {
          categoryGroup.templates.forEach(template => {
            flatTemplates.push(template);
          });
        });
        setTemplateDocuments(flatTemplates);
      } else {
        throw new Error(response.error || 'Failed to load templates');
      }
    } catch (err) {
      console.error('Error fetching templates:', err);
      setError('Failed to load template documents');
    } finally {
      setLoading(prev => ({ ...prev, templates: false }));
    }
  };

  const fetchAllPhaseDocuments = async () => {
    setLoading(prev => ({ ...prev, phaseOne: true, phaseTwo: true }));
    setError(null);
    try {
      const response = await fetchDocumentsByPhase(50); // Fetch up to 50 documents
      
      if (response.success) {
        // Phase I documents are already processed with metadata
        setPhaseOneDocuments(response.phaseOne.map(doc => ({
          ...doc,
          name: `${doc.humanType}${doc.date ? ` - ${doc.date}` : ''}`,
          createdAt: doc.lastModified,
          hasEdits: response.phaseTwo.some(edited => 
            edited.originalKey === doc.key
          ),
          editCount: response.phaseTwo.filter(edited => 
            edited.originalKey === doc.key
          ).length
        })));
        
        // Phase II documents are already processed with changes
        setPhaseTwoDocuments(response.phaseTwo.map(doc => ({
          ...doc,
          name: `${doc.humanType} (Edited)${doc.date ? ` - ${doc.date}` : ''}`,
          editedAt: doc.lastModified
        })));
      } else {
        throw new Error(response.error || 'Failed to load documents');
      }
      
    } catch (err) {
      console.error('Error fetching phase documents:', err);
      setError('Failed to load documents. Please try again.');
    } finally {
      setLoading(prev => ({ ...prev, phaseOne: false, phaseTwo: false }));
    }
  };

  const handleDownload = async (docKey, docName) => {
    try {
      console.log('Downloading document:', { docKey, sessionId });

      // Use authenticated download with sessionId
      const result = await downloadDocumentAuthenticated(docKey, false, sessionId);

      if (!result.success) {
        throw new Error(result.error || 'Download failed');
      }
    } catch (error) {
      console.error('Error downloading document:', error);
      alert(`Failed to download ${docName}. Please try again.`);
    }
  };

  const handleEditAgain = async (docKey) => {
    try {
      // Navigate to chat interface with the document loaded for editing
      // This would typically be handled by parent component or router
      console.log('Initiating edit session for:', docKey);
      
      // You could emit an event or call a parent callback here
      // For example:
      // onEditDocument(docKey);
      
      // For now, we'll just show an alert
      alert(`Edit functionality will open document ${docKey} in the chat interface.`);
    } catch (err) {
      console.error('Error initiating edit:', err);
    }
  };

  const handleRefresh = () => {
    loadAllDocuments();
  };

  return (
    <div className="flex h-full">
      {/* Error Banner */}
      {error && (
        <div className="absolute top-0 left-0 right-0 bg-red-100 border-b border-red-400 text-red-700 px-4 py-3 z-10">
          <span className="block sm:inline">{error}</span>
          <button
            onClick={() => setError(null)}
            className="absolute top-0 right-0 px-4 py-3"
          >
            <span className="text-2xl">&times;</span>
          </button>
        </div>
      )}

      {/* Template Library - 25% width */}
      <div className="w-1/4 border-r p-4 overflow-auto bg-gray-50">
        <div className="flex justify-between items-center mb-4">
          <h3 className="font-bold text-lg">Template Library</h3>
        </div>
        {loading.templates ? (
          <div className="text-center py-4">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500 mx-auto"></div>
          </div>
        ) : (
          <div className="space-y-2">
            {/* Group templates by category */}
            {Object.entries(
              templateDocuments.reduce((acc, template) => {
                const category = template.category || 'Other';
                if (!acc[category]) acc[category] = [];
                acc[category].push(template);
                return acc;
              }, {})
            ).map(([category, templates]) => (
              <div key={category} className="mb-4">
                <h4 className="text-sm font-semibold text-gray-600 mb-2">{category}</h4>
                {templates.map((template) => (
                  <div 
                    key={template.key} 
                    className="border rounded p-3 mb-2 hover:bg-white hover:shadow-sm cursor-pointer transition-all"
                    onClick={() => setExpandedTemplate(expandedTemplate === template.key ? null : template.key)}
                  >
                    <p className="text-sm font-medium">{template.name}</p>
                    {expandedTemplate === template.key && (
                      <p className="text-xs text-gray-600 mt-1">{template.description}</p>
                    )}
                  </div>
                ))}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Documents Created - 37.5% width */}
      <div className="w-3/8 border-r p-4 overflow-auto" style={{ width: '37.5%' }}>
        <div className="flex justify-between items-center mb-4">
          <h3 className="font-bold text-lg">Documents Created</h3>
          <button
            onClick={handleRefresh}
            className="text-sm text-blue-600 hover:text-blue-800"
            title="Refresh documents"
          >
            ↻ Refresh
          </button>
        </div>
        {loading.phaseOne ? (
          <div className="text-center py-4">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500 mx-auto"></div>
          </div>
        ) : phaseOneDocuments.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <p>No documents created yet</p>
          </div>
        ) : (
          <div className="space-y-3">
            {phaseOneDocuments.map((doc) => (
              <div key={doc.key} className="border rounded p-3 hover:shadow-md transition-shadow">
                <div className="flex justify-between items-start">
                  <div className="flex-grow">
                    <p className="font-medium">{doc.name}</p>
                    <p className="text-xs text-gray-500 mt-1">
                      Created: {new Date(doc.createdAt).toLocaleString()}
                    </p>
                    {doc.hasEdits && (
                      <p className="text-xs text-green-600 mt-1">
                        ✓ {doc.editCount} edit{doc.editCount !== 1 ? 's' : ''} available
                      </p>
                    )}
                  </div>
                  <span className={`text-xs px-2 py-1 rounded ${
                    doc.humanType === 'Will' ? 'bg-blue-100 text-blue-700' :
                    doc.humanType === 'Trust' ? 'bg-purple-100 text-purple-700' :
                    doc.humanType === 'Power of Attorney' ? 'bg-orange-100 text-orange-700' :
                    'bg-gray-100 text-gray-700'
                  }`}>
                    {doc.humanType}
                  </span>
                </div>
                <button
                  onClick={() => handleDownload(doc.key, doc.name)}
                  className="mt-2 px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700"
                >
                  Download
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* AI-Edited Documents - 37.5% width */}
      <div className="w-3/8 p-4 overflow-auto" style={{ width: '37.5%' }}>
        <h3 className="font-bold text-lg mb-4">AI-Edited Documents</h3>
        {loading.phaseTwo ? (
          <div className="text-center py-4">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500 mx-auto"></div>
          </div>
        ) : phaseTwoDocuments.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <p>No edited documents yet</p>
            <p className="text-sm mt-2">Use the chat to edit your documents</p>
          </div>
        ) : (
          <div className="space-y-3">
            {phaseTwoDocuments.map((doc) => (
              <div key={doc.key} className="border rounded p-3 bg-green-50 hover:shadow-md transition-shadow">
                <div className="flex justify-between items-start">
                  <div className="flex-grow">
                    <p className="font-medium">{doc.name}</p>
                    <p className="text-xs text-gray-500 mt-1">
                      Edited: {new Date(doc.editedAt).toLocaleString()}
                    </p>
                    {doc.originalKey && (
                      <p className="text-xs text-gray-500">
                        Based on: Original document
                      </p>
                    )}
                  </div>
                  <span className="text-xs px-2 py-1 rounded bg-green-100 text-green-700">
                    AI Edited
                  </span>
                </div>
                
                {/* Changes narrative */}
                <div className="mt-3 bg-white rounded p-2">
                  <p className="text-sm font-medium mb-1 text-gray-700">Changes made:</p>
                  <ul className="text-sm text-gray-600 space-y-1">
                    {doc.changes.map((change, idx) => (
                      <li key={idx} className="pl-3 relative">
                        <span className="absolute left-0 text-green-600">✓</span>
                        {change}
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="mt-3 flex space-x-2">
                  <button
                    onClick={() => handleDownload(doc.key, doc.name)}
                    className="px-3 py-1 bg-green-600 text-white text-sm rounded hover:bg-green-700 flex items-center"
                  >
                    <span className="mr-1">⬇</span> Download
                  </button>
                  <button
                    onClick={() => handleEditAgain(doc.key)}
                    className="px-3 py-1 bg-gray-600 text-white text-sm rounded hover:bg-gray-700 flex items-center"
                  >
                    <span className="mr-1">✏</span> Edit Again
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default MissionControlCenter;